import '@/styles/specialoffert-layout.css';
import '@/styles/battlepass-layout.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { SilkConfirmModal, type SilkConfirmPhase } from '@/components/SilkConfirmModal';
import { useLocale } from '@/context/LocaleContext';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import {
  useSpecialOfferMutations,
  useSpecialOffers,
  type SpecialOfferItem,
} from '@/hooks/useSpecialOffer';
import { itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';

interface BuyDialog {
  offer: SpecialOfferItem;
  phase: SilkConfirmPhase;
  message?: string;
}

function OfferBox({
  offer,
  onBuy,
  processingCode,
  t,
}: {
  offer: SpecialOfferItem;
  onBuy: (offer: SpecialOfferItem) => void;
  processingCode: string | null;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const isProcessing = processingCode === offer.codename128;
  const soldOut = offer.available <= 0;
  return (
    <article className={`specialoffert-box${offer.recommend ? ' recommended' : ''}${soldOut ? ' soldout' : ''}`}>
      <header className="offer-card-header">
        <span>{offer.recommend ? <><i className="fa fa-star" aria-hidden="true" /> {t('so_recommended')}</> : t('special_offer')}</span>
        <span className="offer-stock">{soldOut ? t('so_sold_out') : `${t('so_available')}: ${offer.available}`}</span>
      </header>
      <div className="offer-product">
        <div className="specialoffert-img">
          <img src={resolveMediaUrl(offer.url_image)} alt="" loading="lazy" onError={e=>{e.currentTarget.onerror=null;e.currentTarget.src=itemIconFallbackUrl();}} />
          <span className="specialoffert-amount-on-img">×{offer.quanty}</span>
        </div>
        <h2 className="specialoffert-item-name">{offer.sn_name}</h2>
        {offer.cost_sold < offer.cost && <div className="offer-discount"><del>{offer.cost.toLocaleString()} Silks</del><span>−{offer.discount_percent}%</span></div>}
        <div className="specialoffert-price">{(offer.cost_sold ?? offer.cost).toLocaleString()} <span>Silks</span></div>
      </div>
      <div className="offer-expiry"><i className="fa fa-clock-o" aria-hidden="true" /><span>{t('so_offer_ends_in')}</span><strong>{offer.time_left}</strong></div>
      <footer className="offer-card-footer">
        <button type="button" className="specialoffert-buy-btn" data-codename={offer.codename128} disabled={soldOut || isProcessing} onClick={()=>onBuy(offer)}>
          <i className={`fa fa-${soldOut ? 'ban' : 'shopping-cart'}`} aria-hidden="true" /> {soldOut ? t('so_sold_out') : isProcessing ? t('so_processing') : t('so_buy')}
        </button>
      </footer>
    </article>
  );
}

export function SpecialOfferPage() {
  const { t } = useLocale();
  const { data: offers = [], isLoading, isError } = useSpecialOffers();
  const { buyOffer } = useSpecialOfferMutations();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const update = () => setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener('scroll', update, { passive: true });
    return () => { observer.disconnect(); el.removeEventListener('scroll', update); };
  }, [offers.length, isError]);
  const scrollOffers = (direction: number) => {
    const el = carouselRef.current;
    if (el) el.scrollBy({ left: direction * 258, behavior: 'smooth' });
  };


  const { flash, showFlash } = useIngameFlash(3000);
  const [buyDialog, setBuyDialog] = useState<BuyDialog | null>(null);
  const [processingCode, setProcessingCode] = useState<string | null>(null);

  const silkModalOpen = buyDialog?.phase === 'confirm';
  const { data: silkBalance, isFetching: silkLoading, isError: silkError } = useSilkBalance(silkModalOpen);

  const handleBuyClick = useCallback((offer: SpecialOfferItem) => {
    setBuyDialog({ offer, phase: 'confirm' });
  }, []);

  const handleConfirmBuy = useCallback(async () => {
    if (!buyDialog) return;

    const { offer } = buyDialog;
    setBuyDialog({ ...buyDialog, phase: 'pending', message: t('silk_confirm_processing_msg') });
    setProcessingCode(offer.codename128);

    try {
      const result = await buyOffer.mutateAsync({
        codename128: offer.codename128,
        quantity: offer.quanty,
        cost: offer.cost_sold ?? offer.cost,
      });

      if (result.success) {
        setBuyDialog({
          offer,
          phase: 'success',
          message: result.message || t('so_purchase_success'),
        });
        showFlash(result.message || t('so_purchase_success'), 'success');
      } else {
        setBuyDialog({
          offer,
          phase: 'error',
          message: result.message || t('so_purchase_error'),
        });
      }
    } catch {
      setBuyDialog({
        offer: buyDialog.offer,
        phase: 'error',
        message: t('so_network_error'),
      });
    } finally {
      setProcessingCode(null);
    }
  }, [buyDialog, buyOffer, showFlash, t]);

  const closeBuyDialog = useCallback(() => {
    setBuyDialog(null);
    setProcessingCode(null);
  }, []);

  if (isError && offers.length === 0) {
    return (
      <div className="container-ingame fade-in">
        <SimpleMenu title={t('special_offer')} />
        <div className="specialoffert-container">
          <div className="specialoffert-grid">{t('so_network_error')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-ingame fade-in">
      <IngameFlash flash={flash} />

      <div className="specialoffert-container">
        <SimpleMenu title={t('special_offer')} />

        <div className="specialoffert-grid">
          {(edges.left || edges.right) && <button type="button" className="offer-carousel-arrow offer-carousel-arrow--left" disabled={!edges.left} aria-label={t('cal_prev')} onClick={() => scrollOffers(-1)}><i className="fa fa-angle-double-left" aria-hidden="true" /></button>}
          <div className="specialoffert-carousel" ref={carouselRef}>
            {isLoading && offers.length === 0 ? (
              <div className="specialoffert-box">{t('loading_data')}</div>
            ) : offers.length === 0 ? (
              <div className="specialoffert-box">{t('so_no_offers')}</div>
            ) : (
              offers.map((offer) => (
                <OfferBox
                  key={offer.id}
                  offer={offer}
                  onBuy={handleBuyClick}
                  processingCode={processingCode}
                  t={t}
                />
              ))
            )}
          </div>
          {(edges.left || edges.right) && <button type="button" className="offer-carousel-arrow offer-carousel-arrow--right" disabled={!edges.right} aria-label={t('cal_next')} onClick={() => scrollOffers(1)}><i className="fa fa-angle-double-right" aria-hidden="true" /></button>}
        </div>
      </div>

      {buyDialog && (
        <SilkConfirmModal
          open
          className="silk-modal--aaa"
          phase={buyDialog.phase}
          title={t('so_confirm_title')}
          message={t('so_confirm_message', { name: buyDialog.offer.sn_name })}
          cost={buyDialog.offer.cost_sold ?? buyDialog.offer.cost}
          balance={buyDialog.phase === 'confirm' && silkLoading ? null : (silkBalance ?? null)}
          balanceLoading={buyDialog.phase === 'confirm' && silkLoading}
          balanceError={buyDialog.phase === 'confirm' && silkError}
          resultMessage={buyDialog.message}
          confirmLabel={t('so_buy')}
          onConfirm={() => void handleConfirmBuy()}
          onClose={closeBuyDialog}
        />
      )}
    </div>
  );
}
